// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  Controller,
  type Control,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { Slider } from '@mui/material';
import { Label } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import type { OnboardingQuestion } from '@shared/advisory/account-workflow/types';

export interface QuestionSliderProps<TFormData extends FieldValues> {
  question: OnboardingQuestion;
  control: Control<TFormData>;
  name: Path<TFormData>;
}

export const QuestionSlider = <TFormData extends FieldValues>({
  question,
  control,
  name,
}: QuestionSliderProps<TFormData>) => {
  return (
    <div data-testid={`question-${question.id}`}>
      <fieldset className="space-y-6">
        <Label
          component="legend"
          className="text-lg font-medium !leading-6 !text-secondary-dark"
          required
          aria-required="true"
        >
          <Translation
            i18nKey={question.label[0] as any}
            ns={question.label[1].ns}
          />
        </Label>

        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <div className="space-y-2">
              <Slider
                value={field.value ?? 0}
                onChange={(_, value) => field.onChange(value)}
                min={0}
                max={100}
                valueLabelDisplay="off"
                valueLabelFormat={value => `${value}%`}
                data-testid={`slider-${question.id}`}
                className="!mx-2 !p-0"
                color="secondary"
              />
              <div className="text-center text-base font-semibold text-secondary-dark">
                {field.value ?? 0}%
              </div>
            </div>
          )}
        />
      </fieldset>
    </div>
  );
};
